import { APIStatus } from '@application/enums/api-status';
import { createSlice } from '@reduxjs/toolkit';
import { createMVPAsync, createExternalMVPsAsync, deleteMVPAsync, downloadExcelTemplateAsync, getAllMVPsAsync, getMVPByIdAsync, updateMVPAsync, updateExternalMVPAsync, uploadExcelAsync, sendInvitationAsync, sendInvitationsBulkAsync, sendReinvitationEmailAsync } from './mvpsThunk';
const initialState = {
    status: APIStatus.IDLE,
    mvps: null,
    invitationResponse: null,
    selectedMVP: null,
    searchString: null
};
export const mvpsSlice = createSlice({
    name: 'mvps',
    initialState,
    reducers: {
        resetMVPsApiState(state) {
            state.status = APIStatus.IDLE;
            state.apiError = undefined;
        },
        clearSelectedMVP(state) {
            state.selectedMVP = null;
        },
        resetSearchString(state) {
            state.searchString = null;
        },
        setSearchString(state, action) {
            state.searchString = action.payload;
        }
    },
    extraReducers: (builder) => {
        builder
            // Get All
            .addCase(getAllMVPsAsync.pending, (state) => {
            state.status = APIStatus.PENDING;
            state.apiError = undefined;
        })
            .addCase(getAllMVPsAsync.fulfilled, (state, action) => {
            state.status = APIStatus.SUCCESS;
            state.mvps = action.payload;
        })
            .addCase(getAllMVPsAsync.rejected, (state, action) => {
            state.status = APIStatus.FAILED;
            if (action.payload)
                state.apiError = action.payload;
        })
            // Create
            .addCase(createMVPAsync.pending, (state) => {
            state.status = APIStatus.PENDING;
            state.apiError = undefined;
        })
            .addCase(createMVPAsync.fulfilled, (state, action) => {
            state.status = APIStatus.SUCCESS;
            if (state.mvps && Array.isArray(state.mvps.items) && action.payload.data) {
                state.mvps.items.push(action.payload.data);
            }
        })
            .addCase(createMVPAsync.rejected, (state, action) => {
            state.status = APIStatus.FAILED;
            state.apiError = action.payload;
        })
            // Create External
            .addCase(createExternalMVPsAsync.pending, (state) => {
            state.status = APIStatus.PENDING;
            state.apiError = undefined;
        })
            .addCase(createExternalMVPsAsync.fulfilled, (state, action) => {
            state.status = APIStatus.SUCCESS;
            if (state.mvps && Array.isArray(state.mvps.items) && action.payload) {
                state.mvps.items.push(...action.payload);
            }
        })
            .addCase(createExternalMVPsAsync.rejected, (state, action) => {
            state.status = APIStatus.FAILED;
            state.apiError = action.payload;
        })
            // Get By Id
            .addCase(getMVPByIdAsync.pending, (state) => {
            state.status = APIStatus.PENDING;
            state.apiError = undefined;
        })
            .addCase(getMVPByIdAsync.fulfilled, (state, action) => {
            state.status = APIStatus.SUCCESS;
            state.selectedMVP = action.payload;
        })
            .addCase(getMVPByIdAsync.rejected, (state, action) => {
            state.status = APIStatus.FAILED;
            state.apiError = action.payload;
        })
            // Update
            .addCase(updateMVPAsync.pending, (state) => {
            state.status = APIStatus.PENDING;
            state.apiError = undefined;
        })
            .addCase(updateMVPAsync.fulfilled, (state, action) => {
            state.status = APIStatus.SUCCESS;
            if (state.mvps && Array.isArray(state.mvps.items) && action.payload.data) {
                const data = action.payload.data;
                const index = state.mvps.items.findIndex((x) => x.id === data.id);
                if (index !== -1)
                    state.mvps.items[index] = data;
                state.selectedMVP = data;
            }
            else {
                if (action.payload.data)
                    state.selectedMVP = action.payload.data;
            }
        })
            .addCase(updateMVPAsync.rejected, (state, action) => {
            state.status = APIStatus.FAILED;
            state.apiError = action.payload;
        })
            // Update External
            .addCase(updateExternalMVPAsync.pending, (state) => {
            state.status = APIStatus.PENDING;
            state.apiError = undefined;
        })
            .addCase(updateExternalMVPAsync.fulfilled, (state, action) => {
            state.status = APIStatus.SUCCESS;
            if (state.mvps && Array.isArray(state.mvps.items)) {
                const data = action.payload;
                const index = state.mvps.items.findIndex((x) => x.id === data.id);
                if (index !== -1)
                    state.mvps.items[index] = data;
                state.selectedMVP = data;
            }
            else {
                state.selectedMVP = action.payload;
            }
        })
            .addCase(updateExternalMVPAsync.rejected, (state, action) => {
            state.status = APIStatus.FAILED;
            state.apiError = action.payload;
        })
            // Delete
            .addCase(deleteMVPAsync.pending, (state) => {
            state.status = APIStatus.PENDING;
            state.apiError = undefined;
        })
            .addCase(deleteMVPAsync.fulfilled, (state, action) => {
            state.status = APIStatus.SUCCESS;
            if (state.mvps && Array.isArray(state.mvps.items)) {
                const data = action.payload;
                state.mvps.items = state.mvps.items.filter((x) => x.id !== data.id);
                // Update total count if available
                if (state.mvps.totalCount !== undefined) {
                    state.mvps.totalCount = Math.max(0, state.mvps.totalCount - 1);
                }
            }
        })
            .addCase(deleteMVPAsync.rejected, (state, action) => {
            state.status = APIStatus.FAILED;
            state.apiError = action.payload;
        })
            // Download Excel Template
            .addCase(downloadExcelTemplateAsync.pending, (state) => {
            state.status = APIStatus.PENDING;
            state.apiError = undefined;
        })
            .addCase(downloadExcelTemplateAsync.fulfilled, (state) => {
            state.status = APIStatus.SUCCESS;
        })
            .addCase(downloadExcelTemplateAsync.rejected, (state, action) => {
            state.status = APIStatus.FAILED;
            if (action.payload)
                state.apiError = action.payload;
        })
            // Upload Excel
            .addCase(uploadExcelAsync.pending, (state) => {
            state.status = APIStatus.PENDING;
            state.apiError = undefined;
        })
            .addCase(uploadExcelAsync.fulfilled, (state) => {
            state.status = APIStatus.SUCCESS;
        })
            .addCase(uploadExcelAsync.rejected, (state, action) => {
            state.status = APIStatus.FAILED;
            if (action.payload)
                state.apiError = action.payload;
        })
            // Send Invitation
            .addCase(sendInvitationAsync.pending, (state) => {
            state.status = APIStatus.PENDING;
            state.apiError = undefined;
        })
            .addCase(sendInvitationAsync.fulfilled, (state, action) => {
            state.status = APIStatus.SUCCESS;
            state.invitationResponse = action.payload;
        })
            .addCase(sendInvitationAsync.rejected, (state, action) => {
            state.status = APIStatus.FAILED;
            if (action.payload)
                state.apiError = action.payload;
        })
            // Send Re-invitation Email
            .addCase(sendReinvitationEmailAsync.pending, (state) => {
            state.status = APIStatus.PENDING;
            state.apiError = undefined;
        })
            .addCase(sendReinvitationEmailAsync.fulfilled, (state, action) => {
            state.status = APIStatus.SUCCESS;
            state.invitationResponse = action.payload;
        })
            .addCase(sendReinvitationEmailAsync.rejected, (state, action) => {
            state.status = APIStatus.FAILED;
            if (action.payload)
                state.apiError = action.payload;
        })
            // Send Invitations Bulk
            .addCase(sendInvitationsBulkAsync.pending, (state) => {
            state.status = APIStatus.PENDING;
            state.apiError = undefined;
        })
            .addCase(sendInvitationsBulkAsync.fulfilled, (state, action) => {
            state.status = APIStatus.SUCCESS;
        })
            .addCase(sendInvitationsBulkAsync.rejected, (state, action) => {
            state.status = APIStatus.FAILED;
            if (action.payload)
                state.apiError = action.payload;
        });
    }
});
export const { resetMVPsApiState, clearSelectedMVP, resetSearchString, setSearchString } = mvpsSlice.actions;
export default mvpsSlice.reducer;
